import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { getMasterData } from "../../redux/actions/appActions";
import { SET_CITIES, SET_DEPARTMENTS } from "../../redux/actions/actionTypes";
import { getKeyValuePairs } from "../../redux/services/utils";
import { IconButton, Typography } from "@mui/material";
import Close from "@mui/icons-material/Close";
import MultiSelect from "./MultiSelect";

type FormValues = Yup.InferType<any>;

const defaultValues = {
  function: null,
  location: [],
};

const schema = Yup.object().shape({
  function: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .notRequired(),
  location: Yup.array(),
});

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const SearchFilter = (props: any) => {
  const { handleSaveFilter, open, setOpen, filters } = props;
  const dispatch = useDispatch();
  const { departments, cities } = useSelector((state: any) => state.app);

  useEffect(() => {
    if (!cities?.length) {
      dispatch(getMasterData("CITY", SET_CITIES));
    }
    if (!departments?.length) {
      dispatch(getMasterData("DEPARTMENTS", SET_DEPARTMENTS));
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const { handleSubmit, control, reset, setValue } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (filters?.function.length) {
      setValue("function", { label: filters.function?.[0], value: filters?.function?.[0] });
    } else {
      setValue("function", null);
    }

    setValue("location", getKeyValuePairs(filters.location));
  }, [filters]);

  const onSubmit = (data: FormValues) => {
    const payload = {
      ...filters,
      function: data?.function?.value ? [data?.function.value] : [],
      location: data?.location?.map?.((el: any) => el?.value) || [],
    };

    handleSaveFilter(payload);
  };

  const resetFilters = () => {
    reset();
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullScreen
        PaperProps={{
          sx: {
            marginTop: 20,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              fontSize: "14px",
              letterSpacing: "1.67px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            id="customized-dialog-title"
          >
            <Typography sx={{ fontWeight: 600 }}>Search</Typography>
            <Box
              aria-label="close"
              onClick={handleClose}
              sx={{
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                color: "#756DE9",
                fontWeight: "600",
              }}
            >
              CLOSE
              <IconButton
                size="small"
                sx={{
                  color: "#756DE9",
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              mb: 2,
              overflow: "visible",
              paddingBottom: "16px",
            }}
          >
            <Box display="flex" flexDirection="column">
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Function
              </Typography>
              <MultiSelect
                multiple={false}
                noError
                name="function"
                control={control}
                options={getKeyValuePairs(departments)}
              />
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Location
              </Typography>
              <MultiSelect noError name="location" control={control} options={getKeyValuePairs(cities)} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                backgroundColor: "white",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "50%",
                color: "#000000",
                "&:hover ": {
                  backgroundColor: "#f5f5f5", // Change hover background color if needed
                },
              }}
              variant="contained"
              size="small"
              style={{ padding: "15px 20px" }}
              className="w-100 mt-2 btn-pad"
              onClick={resetFilters}
            >
              Clear Filter
            </Button>

            <Button
              sx={{
                backgroundColor: "#756DE9",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "50%",
                paddingY: "15px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              variant="contained"
              size="small"
              className="w-100 mt-2 btn-pad"
              type="submit"
            >
              Search
            </Button>
          </DialogActions>
        </Box>
      </BootstrapDialog>
    </>
  );
};

export default SearchFilter;
